import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { ArrowLeft, Plus, Trash2, Loader2, Save, X, Search, CheckCircle } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import { toast } from 'sonner';
import api from '../../utils/api';
import { EtapaSelect, useEtapasProyecto } from './EtapaSelect';

const TIPOS_UNIDAD = ['Departamento', 'Estacionamiento', 'Depósito', 'Local Comercial', 'Área Común'];
const TIPOS_DOCUMENTO = ['DNI', 'CE', 'Pasaporte', 'RUC'];

const EMPTY_COPROPIETARIO = { nombre: '', tipo_documento: '', numero_documento: '', email: '', telefono: '' };
const EMPTY_UNIDAD = { codigo: '', nombre: '', tipo_unidad: '' };

const EntregaNueva = () => {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [proyectos, setProyectos] = useState([]);
  const [buscandoCRM, setBuscandoCRM] = useState(false);
  const [datosCRM, setDatosCRM] = useState(null); // Store CRM data for submission

  const [form, setForm] = useState({
    proyecto_id: '',
    proyecto_nombre: '',
    subdivision_id: '',
    subdivision_nombre: '',
    proforma_codigo: '',
    unidad_principal: { codigo: '', nombre: '', tipo_unidad: '' },
    unidades_asignadas: [],
    fecha_entrega_contractual: '',
    cliente_nombre: '',
    cliente_tipo_documento: '',
    cliente_numero_documento: '',
    cliente_email: '',
    cliente_telefono_fijo: '',
    cliente_celular: '',
    co_propietarios: [],
    premio: '',
    notas: ''
  });

  const [newUnidad, setNewUnidad] = useState({ ...EMPTY_UNIDAD });
  const etapasInfo = useEtapasProyecto(form.proyecto_id, form.unidad_principal.codigo?.trim());
  const requiereEtapa = etapasInfo.etapas.length > 0;

  useEffect(() => {
    if (etapasInfo.cargando) return;
    setForm(prev => {
      const vigente = etapasInfo.etapas.find(e => e.id === prev.subdivision_id);
      if (vigente) return prev.subdivision_nombre === vigente.nombre ? prev : { ...prev, subdivision_nombre: vigente.nombre };
      const sugerida = etapasInfo.etapas.find(e => e.id === etapasInfo.sugerida_id);
      return { ...prev, subdivision_id: sugerida?.id || '', subdivision_nombre: sugerida?.nombre || '' };
    });
  }, [etapasInfo]);

  useEffect(() => {
    api.get('/proyectos').then(r => setProyectos(r.data || [])).catch(() => {});
  }, []);

  const updateField = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleProyectoChange = (proyectoId) => {
    const proy = proyectos.find(p => p.id === proyectoId);
    setForm(prev => ({
      ...prev,
      proyecto_id: proyectoId,
      proyecto_nombre: proy?.nombre || ''
    }));
  };

  const handleBuscarCRM = async () => {
    const proforma = form.proforma_codigo.trim();
    if (!proforma) {
      toast.error('Ingrese un código de proforma para buscar');
      return;
    }
    setBuscandoCRM(true);
    try {
      const resp = await api.post('/entregas/crm/consultar', { proforma_codigo: proforma });
      const { datos_formulario, datos_crm, pdfs_disponibles, raw_payload } = resp.data;

      // Pre-fill form fields
      setForm(prev => ({
        ...prev,
        proyecto_nombre: datos_formulario.proyecto_nombre || prev.proyecto_nombre,
        subdivision_id: datos_formulario.subdivision_id || prev.subdivision_id,
        subdivision_nombre: datos_formulario.subdivision_nombre || prev.subdivision_nombre,
        unidad_principal: datos_formulario.unidad_principal || prev.unidad_principal,
        unidades_asignadas: datos_formulario.unidades_asignadas || prev.unidades_asignadas,
        cliente_nombre: datos_formulario.cliente_nombre || prev.cliente_nombre,
        cliente_tipo_documento: datos_formulario.cliente_tipo_documento || prev.cliente_tipo_documento,
        cliente_numero_documento: datos_formulario.cliente_numero_documento || prev.cliente_numero_documento,
        cliente_email: datos_formulario.cliente_email || prev.cliente_email,
        cliente_celular: datos_formulario.cliente_celular || prev.cliente_celular,
        cliente_telefono_fijo: datos_formulario.cliente_telefono_fijo || prev.cliente_telefono_fijo,
        co_propietarios: datos_formulario.co_propietarios || prev.co_propietarios,
        premio: datos_formulario.premio || prev.premio,
      }));

      // Match proyecto from dropdown if available
      if (datos_formulario.proyecto_nombre && proyectos.length > 0) {
        const match = proyectos.find(p => p.nombre.toLowerCase() === datos_formulario.proyecto_nombre.toLowerCase());
        if (match) {
          setForm(prev => ({ ...prev, proyecto_id: match.id }));
        }
      }

      // Store CRM data for submission
      setDatosCRM({
        ...datos_crm,
        crm_raw_payload: raw_payload,
      });

      const numPdfs = (pdfs_disponibles.contrato ? 1 : 0) + (pdfs_disponibles.adendas?.length || 0);
      const numOtros = pdfs_disponibles.otros_documentos?.length || 0;
      const pdfMsg = pdfs_disponibles.sin_ccv
        ? (numOtros > 0 ? ` El CCV no vino identificado; se analizarán ${numOtros} documento(s) para identificarlo tras guardar.` : '')
        : (numPdfs > 0 ? ` ${numPdfs} PDF(s) serán analizados tras guardar.` : '');
      toast.success(`Datos cargados desde CRM.${pdfMsg}`);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Error al consultar el CRM';
      toast.error(typeof detail === 'string' ? detail : JSON.stringify(detail));
      setDatosCRM(null);
    } finally {
      setBuscandoCRM(false);
    }
  };

  const addUnidad = () => {
    if (newUnidad.codigo.trim() && newUnidad.tipo_unidad) {
      setForm(prev => ({
        ...prev,
        unidades_asignadas: [...prev.unidades_asignadas, {
          codigo: newUnidad.codigo.trim(),
          nombre: newUnidad.nombre.trim() || null,
          tipo_unidad: newUnidad.tipo_unidad
        }]
      }));
      setNewUnidad({ ...EMPTY_UNIDAD });
    }
  };

  const removeUnidad = (idx) => {
    setForm(prev => ({
      ...prev,
      unidades_asignadas: prev.unidades_asignadas.filter((_, i) => i !== idx)
    }));
  };

  const addCopropietario = () => {
    setForm(prev => ({
      ...prev,
      co_propietarios: [...prev.co_propietarios, { ...EMPTY_COPROPIETARIO }]
    }));
  };

  const updateCopropietario = (idx, field, value) => {
    setForm(prev => {
      const updated = [...prev.co_propietarios];
      updated[idx] = { ...updated[idx], [field]: value };
      return { ...prev, co_propietarios: updated };
    });
  };

  const removeCopropietario = (idx) => {
    setForm(prev => ({
      ...prev,
      co_propietarios: prev.co_propietarios.filter((_, i) => i !== idx)
    }));
  };

  const isValid = () => {
    if (!form.proyecto_nombre.trim()) return false;
    if (proyectos.length > 0 && !form.proyecto_id) return false;
    if (requiereEtapa && !form.subdivision_id) return false;
    if (etapasInfo.cargando) return false;
    if (!form.proforma_codigo.trim()) return false;
    if (!form.unidad_principal.codigo?.trim()) return false;
    if (!form.unidad_principal.tipo_unidad) return false;
    if (!form.cliente_nombre.trim()) return false;
    if (!form.cliente_tipo_documento) return false;
    if (!form.cliente_numero_documento.trim()) return false;
    if (!form.cliente_email.trim() || !form.cliente_email.includes('@')) return false;
    if (!form.cliente_celular.trim()) return false;

    for (const cp of form.co_propietarios) {
      if (!cp.nombre.trim() || !cp.tipo_documento || !cp.numero_documento.trim() ||
          !cp.email.trim() || !cp.email.includes('@') || !cp.telefono.trim()) {
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!isValid()) {
      toast.error('Complete todos los campos obligatorios');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        proyecto_id: form.proyecto_id || undefined,
        subdivision_id: form.subdivision_id || undefined,
        subdivision_nombre: form.subdivision_nombre || undefined,
        fecha_entrega_contractual: form.fecha_entrega_contractual || undefined,
        cliente_telefono_fijo: form.cliente_telefono_fijo || undefined,
        premio: form.premio || undefined,
        notas: form.notas || undefined,
        // Include CRM data if present
        ...(datosCRM ? datosCRM : {}),
      };
      const resp = await api.post('/entregas/', payload);
      const codigo = resp.data?.codigo || '';
      const hasPdfs = datosCRM && resp.data?.calculando_fechas;
      toast.success(`Entrega ${codigo} creada exitosamente${hasPdfs ? '. Las fechas contractuales se están extrayendo de los PDFs.' : ''}`);
      navigate('/entregas');
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail || (
        !err.response
          ? 'Sin conexión con el servidor. Verifique su conexión e intente nuevamente.'
          : `Error del servidor (HTTP ${status}) al crear la entrega. Intente nuevamente; si persiste, informe al administrador.`
      );
      toast.error(typeof detail === 'string' ? detail : JSON.stringify(detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4 max-w-4xl mx-auto" data-testid="nueva-entrega-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/entregas')} data-testid="btn-volver">
          <ArrowLeft size={16} className="mr-1" /> Volver
        </Button>
        <h1 className="text-xl font-bold text-[#2D2D2D]">Nueva Entrega</h1>
      </div>

      {/* CRM Data Banner */}
      {datosCRM && (
        <div className="bg-[#F0F7F6] border border-[#4E7A79]/30 rounded-lg px-4 py-2.5 flex items-center gap-2" data-testid="crm-banner">
          <CheckCircle size={16} className="text-[#4E7A79] shrink-0" />
          <span className="text-sm text-[#4E7A79] font-medium">Datos pre-cargados desde CRM</span>
          {datosCRM.crm_estado_comercial && (
            <Badge variant="outline" className="text-xs border-[#4E7A79]/40 text-[#4E7A79]">
              {datosCRM.crm_estado_comercial}
            </Badge>
          )}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost" size="sm" className="ml-auto h-6 px-2 text-xs text-red-500 hover:text-red-700"
                  onClick={() => setDatosCRM(null)}
                  data-testid="btn-clear-crm"
                >
                  <X size={12} className="mr-1" /> Limpiar CRM
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="max-w-xs text-xs">
                Si haces click aquí, al guardar la entrega, esta se creará como origen = 'manual' en vez de origen = 'crm', y no se lanzará la extracción async de fechas desde PDFs.
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      )}

      {/* Sección A: Ubicación */}
      <Card className="border border-[#E8E8E7]">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold text-[#4E7A79]">A. Ubicación</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Código de Proforma */}
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">Código de Proforma <span className="text-red-500">*</span></Label>
              <div className="flex gap-1.5">
                <Input
                  placeholder="Ej: 2024-19200"
                  value={form.proforma_codigo}
                  onChange={e => updateField('proforma_codigo', e.target.value)}
                  data-testid="input-proforma"
                />
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        size="icon"
                        className="shrink-0"
                        data-testid="btn-buscar-sperant"
                        onClick={handleBuscarCRM}
                        disabled={buscandoCRM || !form.proforma_codigo.trim()}
                      >
                        {buscandoCRM ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Buscar en CRM</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </div>

            {/* Proyecto */}
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">Proyecto <span className="text-red-500">*</span></Label>
              {proyectos.length > 0 ? (
                <Select value={form.proyecto_id} onValueChange={handleProyectoChange}>
                  <SelectTrigger data-testid="select-proyecto">
                    <SelectValue placeholder="Seleccionar proyecto" />
                  </SelectTrigger>
                  <SelectContent>
                    {proyectos.map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  placeholder="Nombre del proyecto"
                  value={form.proyecto_nombre}
                  onChange={e => updateField('proyecto_nombre', e.target.value)}
                  data-testid="input-proyecto-nombre"
                />
              )}
            </div>

            {/* Etapa (solo si el proyecto tiene etapas) */}
            {requiereEtapa && (
              <div className="space-y-1.5" data-testid="campo-etapa">
                <Label className="text-sm font-medium">Etapa <span className="text-red-500">*</span></Label>
                <EtapaSelect
                  etapas={etapasInfo.etapas}
                  value={form.subdivision_id}
                  onChange={e => setForm(prev => ({ ...prev, subdivision_id: e?.id || '', subdivision_nombre: e?.nombre || '' }))}
                />
              </div>
            )}

            {/* Tipo de Unidad Principal */}
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">Tipo de Unidad Principal <span className="text-red-500">*</span></Label>
              <Select
                value={form.unidad_principal.tipo_unidad}
                onValueChange={v => setForm(prev => ({ ...prev, unidad_principal: { ...prev.unidad_principal, tipo_unidad: v } }))}
              >
                <SelectTrigger data-testid="select-tipo-unidad">
                  <SelectValue placeholder="Seleccionar tipo" />
                </SelectTrigger>
                <SelectContent>
                  {TIPOS_UNIDAD.map(t => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Código Unidad Principal */}
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">Código Unidad Principal <span className="text-red-500">*</span></Label>
              <Input
                placeholder="Ej: PEOP-DEPA-4-404"
                value={form.unidad_principal.codigo}
                onChange={e => setForm(prev => ({ ...prev, unidad_principal: { ...prev.unidad_principal, codigo: e.target.value } }))}
                data-testid="input-unidad-principal"
              />
            </div>
          </div>

          {/* Unidades Asignadas */}
          <div className="space-y-1.5">
            <Label className="text-sm font-medium">Unidades Asignadas</Label>
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 items-end">
              <div className="space-y-1">
                <span className="text-[10px] text-[#888]">Tipo *</span>
                <Select value={newUnidad.tipo_unidad} onValueChange={v => setNewUnidad(prev => ({ ...prev, tipo_unidad: v }))}>
                  <SelectTrigger className="h-8 text-sm" data-testid="select-ua-tipo">
                    <SelectValue placeholder="Tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    {TIPOS_UNIDAD.map(t => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <span className="text-[10px] text-[#888]">Código *</span>
                <Input
                  placeholder="Ej: EST-201"
                  value={newUnidad.codigo}
                  onChange={e => setNewUnidad(prev => ({ ...prev, codigo: e.target.value }))}
                  onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addUnidad())}
                  className="h-8 text-sm"
                  data-testid="input-unidad-adicional"
                />
              </div>
              <div className="space-y-1">
                <span className="text-[10px] text-[#888]">Nombre</span>
                <Input
                  placeholder="Opcional"
                  value={newUnidad.nombre}
                  onChange={e => setNewUnidad(prev => ({ ...prev, nombre: e.target.value }))}
                  onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addUnidad())}
                  className="h-8 text-sm"
                  data-testid="input-unidad-nombre"
                />
              </div>
              <Button type="button" variant="outline" size="sm" onClick={addUnidad} className="h-8 shrink-0" data-testid="btn-add-unidad">
                <Plus size={14} className="mr-1" /> Agregar
              </Button>
            </div>
            {form.unidades_asignadas.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {form.unidades_asignadas.map((u, i) => (
                  <Badge key={i} variant="secondary" className="text-xs gap-1 pr-1" data-testid={`badge-ua-${i}`}>
                    {[u.tipo_unidad, u.codigo].filter(Boolean).join(': ')}{u.nombre ? ` (${u.nombre})` : ''}
                    <button onClick={() => removeUnidad(i)} className="hover:text-red-500 ml-0.5">
                      <X size={12} />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </div>

          {/* Fecha de Entrega Contractual */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">Fecha de Entrega Contractual</Label>
              <Input
                type="date"
                value={form.fecha_entrega_contractual}
                onChange={e => updateField('fecha_entrega_contractual', e.target.value)}
                data-testid="input-fecha-entrega"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Sección B: Cliente Titular */}
      <Card className="border border-[#E8E8E7]">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold text-[#4E7A79]">B. Cliente Titular</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">Nombre del Titular <span className="text-red-500">*</span></Label>
              <Input
                placeholder="Apellidos, Nombres"
                value={form.cliente_nombre}
                onChange={e => updateField('cliente_nombre', e.target.value)}
                data-testid="input-cliente-nombre"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">Tipo de Documento <span className="text-red-500">*</span></Label>
              <Select value={form.cliente_tipo_documento} onValueChange={v => updateField('cliente_tipo_documento', v)}>
                <SelectTrigger data-testid="select-tipo-doc">
                  <SelectValue placeholder="Seleccionar" />
                </SelectTrigger>
                <SelectContent>
                  {TIPOS_DOCUMENTO.map(t => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">Número de Documento <span className="text-red-500">*</span></Label>
              <Input
                placeholder="Ej: 44858857"
                value={form.cliente_numero_documento}
                onChange={e => updateField('cliente_numero_documento', e.target.value)}
                data-testid="input-num-doc"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">Email <span className="text-red-500">*</span></Label>
              <Input
                type="email"
                placeholder="email@ejemplo.com"
                value={form.cliente_email}
                onChange={e => updateField('cliente_email', e.target.value)}
                data-testid="input-email"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">Celular <span className="text-red-500">*</span></Label>
              <Input
                type="tel"
                placeholder="+51999888777"
                value={form.cliente_celular}
                onChange={e => updateField('cliente_celular', e.target.value)}
                data-testid="input-celular"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">Teléfono Fijo</Label>
              <Input
                type="tel"
                placeholder="Opcional"
                value={form.cliente_telefono_fijo}
                onChange={e => updateField('cliente_telefono_fijo', e.target.value)}
                data-testid="input-telefono-fijo"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Sección C: Copropietarios */}
      <Card className="border border-[#E8E8E7]">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-semibold text-[#4E7A79]">C. Copropietarios</CardTitle>
            <Button type="button" variant="outline" size="sm" onClick={addCopropietario} data-testid="btn-add-coprop">
              <Plus size={14} className="mr-1" /> Agregar
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {form.co_propietarios.length === 0 ? (
            <p className="text-sm text-[#888] text-center py-4">No hay copropietarios agregados</p>
          ) : (
            <div className="space-y-4">
              {form.co_propietarios.map((cp, idx) => (
                <div key={idx} className="border border-[#E8E8E7] rounded-lg p-3 relative" data-testid={`coprop-${idx}`}>
                  <Button
                    type="button" variant="ghost" size="sm"
                    className="absolute top-2 right-2 text-red-400 hover:text-red-600 h-7 w-7 p-0"
                    onClick={() => removeCopropietario(idx)}
                    data-testid={`btn-remove-coprop-${idx}`}
                  >
                    <Trash2 size={14} />
                  </Button>
                  <p className="text-xs font-medium text-[#888] mb-2">Copropietario {idx + 1}</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Nombre <span className="text-red-500">*</span></Label>
                      <Input
                        placeholder="Nombre completo"
                        value={cp.nombre}
                        onChange={e => updateCopropietario(idx, 'nombre', e.target.value)}
                        className="text-sm"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Tipo Doc. <span className="text-red-500">*</span></Label>
                      <Select value={cp.tipo_documento} onValueChange={v => updateCopropietario(idx, 'tipo_documento', v)}>
                        <SelectTrigger className="text-sm"><SelectValue placeholder="Tipo" /></SelectTrigger>
                        <SelectContent>
                          {TIPOS_DOCUMENTO.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">N° Documento <span className="text-red-500">*</span></Label>
                      <Input
                        placeholder="Número"
                        value={cp.numero_documento}
                        onChange={e => updateCopropietario(idx, 'numero_documento', e.target.value)}
                        className="text-sm"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Email <span className="text-red-500">*</span></Label>
                      <Input
                        type="email" placeholder="email@ejemplo.com"
                        value={cp.email}
                        onChange={e => updateCopropietario(idx, 'email', e.target.value)}
                        className="text-sm"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Teléfono <span className="text-red-500">*</span></Label>
                      <Input
                        type="tel" placeholder="+51999888777"
                        value={cp.telefono}
                        onChange={e => updateCopropietario(idx, 'telefono', e.target.value)}
                        className="text-sm"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Sección D: Información Adicional */}
      <Card className="border border-[#E8E8E7]">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold text-[#4E7A79]">D. Información Adicional</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">Premio</Label>
              <Input
                placeholder="Premio u obsequio (opcional)"
                value={form.premio}
                onChange={e => updateField('premio', e.target.value)}
                data-testid="input-premio"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm font-medium">Notas</Label>
            <Textarea
              placeholder="Notas adicionales (opcional)"
              value={form.notas}
              onChange={e => updateField('notas', e.target.value)}
              rows={3}
              data-testid="input-notas"
            />
          </div>
        </CardContent>
      </Card>

      {/* Botones */}
      <div className="flex items-center justify-end gap-3 pb-8">
        <Button variant="outline" onClick={() => navigate('/entregas')} data-testid="btn-cancelar">
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={saving || !isValid()}
          className="bg-[#9d8272] hover:bg-[#8a7162] text-white min-w-[140px]"
          data-testid="btn-guardar"
        >
          {saving ? <><Loader2 size={16} className="animate-spin mr-1" /> Guardando...</> : <><Save size={16} className="mr-1" /> Guardar Entrega</>}
        </Button>
      </div>
    </div>
  );
};

export default EntregaNueva;
